import type { SubscriberDecision, SubscriberFlag, SubscriberRow } from '@/api/subscribers'

/** Chip tone: red = money at risk, amber = attention soon, gray = info, green = healthy. */
export type Tone = 'red' | 'amber' | 'gray' | 'green'

export const TONE_CLASS: Record<Tone, string> = {
  red: 'bg-danger/15 text-danger border-danger/30',
  amber: 'bg-warning/15 text-warning border-warning/30',
  gray: 'bg-navy-hover text-ink-secondary border-navy-border',
  green: 'bg-success/15 text-success border-success/30',
}

export const FLAG_ORDER: SubscriberFlag[] = [
  'in_grace', 'trial_no_card', 'unpaid', 'no_card', 'expired_in_use', 'partial', 'needs_review', 'trial_ending', 'unknown', 'renewal_soon', 'mismatch', 'not_linked',
]

export const FLAG_LABEL: Record<SubscriberFlag, string> = {
  in_grace: 'No card — grace',
  no_card: 'No card',
  unpaid: 'Unpaid',
  partial: 'Partial',
  needs_review: 'Needs review',
  unknown: 'Unknown period',
  trial_ending: 'Trial ends ≤7d',
  expired_in_use: 'Expired, in use',
  mismatch: 'WA mismatch',
  trial_no_card: 'Trial ending, no card',
  renewal_soon: 'Renews ≤14d',
  not_linked: 'Not linked to billing',
}

export const FLAG_TONE: Record<SubscriberFlag, Tone> = {
  in_grace: 'amber',
  no_card: 'red',
  unpaid: 'red',
  expired_in_use: 'red',
  partial: 'amber',
  needs_review: 'amber',
  trial_ending: 'amber',
  unknown: 'amber',
  mismatch: 'gray',
  trial_no_card: 'red',
  renewal_soon: 'gray',
  not_linked: 'gray',
}

export const STATUS_TONE: Record<string, Tone> = {
  active: 'green',
  trial: 'amber',
  trialing: 'amber',
  expired: 'red',
  suspended: 'red',
  cancelled: 'gray',
}

export const DECISION_OPTIONS: { value: SubscriberDecision; label: string; key: string }[] = [
  { value: 'none', label: 'No decision', key: '0' },
  { value: 'charge', label: 'Charge', key: '1' },
  { value: 'extend_trial', label: 'Extend trial', key: '2' },
  { value: 'downgrade', label: 'Downgrade', key: '3' },
  { value: 'suspend', label: 'Suspend', key: '4' },
  { value: 'free', label: 'Free', key: '5' },
  { value: 'follow_up', label: 'Follow up', key: '6' },
]

export const DECISION_LABEL: Record<SubscriberDecision, string> = Object.fromEntries(
  DECISION_OPTIONS.map((o) => [o.value, o.label]),
) as Record<SubscriberDecision, string>

const nf0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const nf2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** ₪ with thousands separators; decimals only when there are agorot (₪135, ₪24.30, ₪1,234). */
export function nis(agorot: number | null | undefined): string {
  if (agorot == null || Number.isNaN(agorot)) return '—'
  const v = agorot / 100
  const sign = v < 0 ? '-' : ''
  const abs = Math.abs(v)
  return `${sign}₪${Number.isInteger(abs) ? nf0.format(abs) : nf2.format(abs)}`
}

/** "₪135 + ₪24.30 VAT = ₪159.30" — never ambiguous. */
export function moneyBreakdown(row: Pick<SubscriberRow, 'net_agorot' | 'vat_agorot' | 'gross_agorot'>): string {
  if (!row.gross_agorot && !row.net_agorot) return '—'
  return `${nis(row.net_agorot)} + ${nis(row.vat_agorot)} VAT = ${nis(row.gross_agorot)}`
}

/** DD/MM/YYYY, independent of locale. */
export function ddmmyyyy(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}/${d.getFullYear()}`
}

export function ddmmyyyyTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${ddmmyyyy(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** "2026-09" → "09/2026" */
export function periodLabel(key: string | null | undefined): string {
  if (!key) return '—'
  const m = /^(\d{4})-(\d{2})/.exec(key)
  return m ? `${m[2]}/${m[1]}` : key
}

/** Lower = needs attention sooner. Red flags first, then amber, then gray, then all good. */
export function attentionScore(row: SubscriberRow): number {
  if (row.flags.includes('in_grace') && row.grace_days_left != null && row.grace_days_left <= 2) return 0
  if (row.flags.some((f) => FLAG_TONE[f] === 'red')) return 0
  if (row.flags.some((f) => FLAG_TONE[f] === 'amber')) return 1
  if (row.flags.length) return 2
  return 3
}

export function sortedFlags(flags: SubscriberFlag[]): SubscriberFlag[] {
  return [...flags].sort((a, b) => FLAG_ORDER.indexOf(a) - FLAG_ORDER.indexOf(b))
}

export function subscriberFlagLabel(flag: SubscriberFlag, row: SubscriberRow): string {
  return flag === 'in_grace' && row.grace_days_left != null
    ? `No card — grace, ${row.grace_days_left} days left`
    : FLAG_LABEL[flag]
}

export function subscriberFlagTone(flag: SubscriberFlag, row: SubscriberRow): Tone {
  return flag === 'in_grace' && row.grace_days_left != null && row.grace_days_left <= 2 ? 'red' : FLAG_TONE[flag]
}

export function isPayingActive(row: SubscriberRow): boolean {
  return row.status === 'active' && row.gross_agorot > 0
}

const DAY = 24 * 60 * 60 * 1000

/** The next date that needs me: trial end for trials, next charge for everyone else. */
export function keyDate(row: SubscriberRow): { at: string | null; kind: 'trial_end' | 'renewal' } {
  const trial = row.status === 'trial' || row.status === 'trialing'
  return trial ? { at: row.trial_end, kind: 'trial_end' } : { at: row.next_charge_at, kind: 'renewal' }
}

export function daysUntil(iso: string | null): number | null {
  if (!iso) return null
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return null
  // Compare calendar days in local time — the same day the date column shows.
  const target = new Date(t.getFullYear(), t.getMonth(), t.getDate())
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((target.getTime() - today.getTime()) / DAY)
}

/** Client-side flags derived from fields already on the row (no server/amount change). */
export function withDerivedFlags(row: SubscriberRow): SubscriberRow {
  const flags = [...row.flags]
  const trial = row.status === 'trial' || row.status === 'trialing'
  const dTrial = daysUntil(row.trial_end)
  if (trial && !row.card_last4 && dTrial != null && dTrial >= 0 && dTrial <= 14) flags.push('trial_no_card')
  const dNext = daysUntil(row.next_charge_at)
  if (row.status === 'active' && dNext != null && dNext >= 0 && dNext <= 14) flags.push('renewal_soon')
  return flags.length === row.flags.length ? row : { ...row, flags }
}
